import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { useLocation, useNavigate, Location, NavigateFunction } from "react-router-dom";
import { IProduct, deleteProduct, getProducts } from "../../../redux/modules/products";
import { RootState } from "../../../redux";
import TypeaheadSearch, { SearchType } from "../../TypeaheadSearch";
import { formatPrice } from "../../../utils/price-utils";
import "./product-list.css";

interface ProductListProps {
  products?: IProduct[];
  count?: number;
  deleteProduct?: (productId: number) => void;
  getProducts?: (page: number, includeArchived?: boolean) => Promise<void>;
  location?: Location;
  navigate?: NavigateFunction;
}

type ProductListState = {
  page: number;
};

class ProductListPage extends React.Component<ProductListProps, ProductListState> {
  constructor(props: ProductListProps) {
    super(props);
    this.state = { page: this.getPageFromUrl() };
  }

  getPageFromUrl = (): number => {
    const params = new URLSearchParams(this.props.location?.search || "");
    const p = parseInt(params.get("page") || "1", 10);
    return Math.max(0, isNaN(p) ? 0 : p - 1);
  };

  getArchivedFromUrl = (): boolean => {
    const params = new URLSearchParams(this.props.location?.search || "");
    return params.get("archived") === "true";
  };

  buildUrl = (page: number, archived: boolean): string => {
    const params = new URLSearchParams();
    params.set("page", String(page + 1));
    if (archived) params.set("archived", "true");
    return `?${params.toString()}`;
  };

  componentDidMount() {
    const page = this.getPageFromUrl();
    const archived = this.getArchivedFromUrl();
    this.props.getProducts && this.props.getProducts(page, archived);
  }

  componentDidUpdate(prevProps: ProductListProps) {
    if (prevProps.location?.search !== this.props.location?.search) {
      const page = this.getPageFromUrl();
      const archived = this.getArchivedFromUrl();
      this.setState({ page });
      this.props.getProducts && this.props.getProducts(page, archived);
    }
  }

  renderProducts() {
    if (!this.props.products) {
      return (
        <div className="product-list__empty">
          <p className="product-list__loading">Триває пошук...</p>
        </div>
      );
    }

    const sorted = [...this.props.products].sort((c1, c2) => (c1.name < c2.name ? -1 : 1));

    if (sorted.length === 0) {
      return (
        <div className="product-list__empty">
          <p className="product-list__empty-text">Товари не знайдені</p>
        </div>
      );
    }

    return sorted.map((product) => (
      <div key={product.id} className={`product-list__card ${product.archived ? "product-list__card--archived" : ""}`}>
        <div className="product-list__card-content">
          <Link to={`/product/${product.id}`} className="product-list__card-link">
            <div className="product-list__card-icon">📦</div>
            <div className="product-list__card-info">
              <h3 className="product-list__card-name">{product.name}</h3>
              <div className="product-list__card-details">
                <div className="product-list__card-stock">
                  <span className="product-list__card-label">На складі:</span>{" "}
                  <span className="product-list__card-amount">
                    {product.amount} {product.unitType === "piece" ? "шт" : "мл"}
                  </span>
                </div>
                <div className="product-list__card-prices">
                  <span className="product-list__card-price-label">Собівартість:</span>{" "}
                  <span className="product-list__card-base-price">{formatPrice(product.basePrice)} грн</span>
                  <span className="product-list__card-separator">•</span>
                  <span className="product-list__card-price-label">Ціна продажу:</span>{" "}
                  <span className="product-list__card-sell-price">{formatPrice(product.sellPrice)} грн</span>
                </div>
                {product.notes && (
                  <p className="product-list__card-notes">
                    <span className="product-list__card-label">Нотатки:</span> {product.notes}
                  </p>
                )}
              </div>
            </div>
          </Link>
          <div className="product-list__card-actions">{this.renderButtons(product)}</div>
        </div>
      </div>
    ));
  }

  renderButtons(product: IProduct) {
    if (!product.archived) {
      return (
        <button type="button" className="product-list__btn-archive" onClick={() => this.archiveProduct(product)} title="Архівувати товар">
          <span className="product-list__btn-icon">🗄️</span>
        </button>
      );
    }
    return <span className="product-list__badge-archived">Архівований</span>;
  }

  render() {
    const showArchived = this.getArchivedFromUrl();

    return (
      <div className="product-list">
        <div className="product-list__header">
          <h1 className="product-list__title">Склад</h1>
          <p className="product-list__subtitle">Управління товарами та запасами</p>
        </div>

        <div className="product-list__actions">
          <Link to="/new-product" className="product-list__btn-add">
            <span className="product-list__btn-add-icon">➕</span>
            Додати Товар
          </Link>
        </div>

        <div className="product-list__search-section">
          <div className="product-list__search-card">
            <h2 className="product-list__search-title">Швидкий пошук</h2>
            <TypeaheadSearch searchType={SearchType.PRODUCTS} />
          </div>
        </div>

        <div className="product-list__filters">
          <div className="product-list__filter-toggle">
            <button
              className={`product-list__filter-btn${!showArchived ? " product-list__filter-btn--active" : ""}`}
              onClick={() => this.setArchived(false)}
            >
              Тільки активні
            </button>
            <button
              className={`product-list__filter-btn${showArchived ? " product-list__filter-btn--active" : ""}`}
              onClick={() => this.setArchived(true)}
            >
              Тільки архівні
            </button>
          </div>
        </div>

        <div className="product-list__content">
          <div className="product-list__grid">{this.renderProducts()}</div>
          {this.renderPagination()}
        </div>
      </div>
    );
  }

  renderPagination = () => {
    const { count } = this.props;
    if (!count) return null;

    const PAGE_SIZE = 20;
    const totalPages = Math.ceil(count / PAGE_SIZE);
    if (totalPages <= 1) return null;

    const current = this.state.page;
    const archived = this.getArchivedFromUrl();
    const pages: (number | "ellipsis")[] = [];

    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      const visible = new Set(
        [0, totalPages - 1, current - 2, current - 1, current, current + 1, current + 2].filter((p) => p >= 0 && p < totalPages)
      );
      const sorted = Array.from(visible).sort((a, b) => a - b);
      for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) pages.push("ellipsis");
        pages.push(sorted[i]);
      }
    }

    return (
      <div className="product-list__pagination">
        <button
          className="product-list__pagination-btn product-list__pagination-btn--nav"
          disabled={current === 0}
          onClick={() => this.goToPage(current - 1, archived)}
        >
          ←
        </button>
        {pages.map((p, i) =>
          p === "ellipsis" ? (
            <span key={`ellipsis-${i}`} className="product-list__pagination-ellipsis">
              …
            </span>
          ) : (
            <button
              key={p}
              className={`product-list__pagination-btn${p === current ? " product-list__pagination-btn--active" : ""}`}
              onClick={() => this.goToPage(p, archived)}
            >
              {p + 1}
            </button>
          )
        )}
        <button
          className="product-list__pagination-btn product-list__pagination-btn--nav"
          disabled={current === totalPages - 1}
          onClick={() => this.goToPage(current + 1, archived)}
        >
          →
        </button>
      </div>
    );
  };

  goToPage = (page: number, archived: boolean) => {
    this.props.navigate && this.props.navigate(this.buildUrl(page, archived));
  };

  setArchived = (archived: boolean) => {
    this.props.navigate && this.props.navigate(this.buildUrl(0, archived));
  };

  archiveProduct = (product: IProduct) => {
    if (window.confirm(`Ви впевнені, що хочете архівувати товар "${product.name}"?`)) {
      this.props.deleteProduct && this.props.deleteProduct(product.id);
    }
  };
}

const mapStateToProps = (state: RootState) => {
  return { products: Object.values(state.productsState.products), count: state.productsState.count };
};

const ConnectedProductListPage = connect(mapStateToProps, { getProducts, deleteProduct })(ProductListPage);

function ProductListPageWithRouter(props: object) {
  const location = useLocation();
  const navigate = useNavigate();
  return <ConnectedProductListPage {...props} location={location} navigate={navigate} />;
}

export default ProductListPageWithRouter;
